(ns app.front.view.admin.tenant-management.user-subs
  "Subscriptions and events for the Users and Service accounts tabs, from the
  `:admin/tenant-users` query. Editing state lives in
  `app.front.view.user-management.state` under the scope `[:tenant tenant-id]`."
  (:require [app.front.view.user-management.model :as model]
            [app.front.view.user-management.state :as state]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

;; ---------------------------------------------------------------------------
;; Fetching
;; ---------------------------------------------------------------------------

(rf/reg-event-fx ::fetch-users
  (fn [{:keys [db]} [_ tenant-id]]
    {:db           (assoc-in db [::data tenant-id :loading?] true)
     :comm/command {:command          :admin/tenant-users
                    :method           :get
                    :data             {:domain-group-id tenant-id}
                    :on-success       [::fetch-users.success tenant-id]
                    :on-error         [::fetch-users.failed tenant-id]
                    :finally-dispatch [::fetch-users.done tenant-id]}}))

;; `:finally-dispatch` follows every response, `:on-error` an unreachable
;; backend (replacing its toast).
(rf/reg-event-db ::fetch-users.done
  (fn [db [_ tenant-id]]
    (assoc-in db [::data tenant-id :loading?] false)))

(rf/reg-event-fx ::fetch-users.failed
  (fn [_ [_ tenant-id]]
    {:fx [[:dispatch [::fetch-users.done tenant-id]]
          [:dispatch [:notifications/add :toast :error
                      :error/connection-error-title :error/connection-error-description]]]}))

(rf/reg-event-db ::fetch-users.success
  (fn [db [_ tenant-id _status {:keys [users user-groups]}]]
    (assoc db ::data (assoc (::data db)
                            tenant-id {:users       users
                                       :user-groups user-groups
                                       :loading?    false}))))

(rf/reg-sub ::tenant-data
  (fn [db [_ tenant-id]]
    (get-in db [::data tenant-id])))

(rf/reg-sub ::loading?
  (fn [[_ tenant-id]] (rf/subscribe [::tenant-data tenant-id]))
  :-> (comp boolean :loading?))

(rf/reg-sub ::user-groups
  (fn [[_ tenant-id]] (rf/subscribe [::tenant-data tenant-id]))
  :-> (comp vec :user-groups))

(rf/reg-sub ::user-groups-by-id
  (fn [[_ tenant-id]] (rf/subscribe [::user-groups tenant-id]))
  (fn [groups _]
    (p/index-by :db/id groups)))

(defn- by-parent
  "Groups the given user-groups by their parent (sub-group), preserving the
  server's ordering so the domain group's own groups come first."
  [groups]
  (->> groups
       (group-by (juxt :parent-group-id :parent-group-name))
       (sort-by (comp second first))
       (map (fn [[[parent-id parent-name] gs]]
              {:parent-group-id   parent-id
               :parent-group-name parent-name
               :groups            (vec (sort-by :group/name gs))}))
       vec))

;; The tenant's groups with their user-groups, the domain group first: one
;; section each in the Users and Service accounts tabs.
(rf/reg-sub ::group-sections
  (fn [[_ tenant-id]] (rf/subscribe [::user-groups tenant-id]))
  (fn [groups [_ tenant-id]]
    (->> (by-parent groups)
         (sort-by (juxt #(not= tenant-id (:parent-group-id %)) :parent-group-name))
         vec)))

(rf/reg-sub ::service-account-sections
  ;; An external module's groups are for people: no service accounts there.
  (fn [[_ tenant-id]] (rf/subscribe [::group-sections tenant-id]))
  (fn [sections _]
    (->> sections
         (remove #(some :group/external-kind (:groups %)))
         vec)))

;; ---------------------------------------------------------------------------
;; Per-group views of the users: a user appears in the section of every group
;; whose user-groups include them, and a search matches any of their groups.
;; ---------------------------------------------------------------------------

(defn- with-group-names [groups-by-id user]
  (assoc user ::group-names (->> (:user/group-ids user)
                                 (keep (comp :group/name groups-by-id))
                                 distinct)))

(rf/reg-sub ::all-users
  (fn [[_ tenant-id]]
    [(rf/subscribe [::tenant-data tenant-id])
     (rf/subscribe [::user-groups-by-id tenant-id])])
  (fn [[data groups-by-id] _]
    (map (partial with-group-names groups-by-id) (:users data))))

(rf/reg-sub ::section-user-group-ids
  (fn [[_ tenant-id]] (rf/subscribe [::user-groups tenant-id]))
  (fn [groups [_ _ parent-group-id]]
    (->> groups
         (filter (comp #{parent-group-id} :parent-group-id))
         (map :db/id)
         set)))

(defn- section-users
  [users parent-group-ids query]
  (let [q (model/normalize-query query)]
    (->> users
         (filter #(some parent-group-ids (:user/group-ids %)))
         (filter #(model/matches? q (::group-names %) %)))))

(rf/reg-sub ::section-persons
  (fn [[_ tenant-id parent-group-id]]
    [(rf/subscribe [::all-users tenant-id])
     (rf/subscribe [::section-user-group-ids tenant-id parent-group-id])])
  (fn [[users parent-group-ids] [_ _ _ query]]
    (-> (remove model/service-account? users)
        (section-users parent-group-ids query))))

(rf/reg-sub ::section-service-accounts
  (fn [[_ tenant-id parent-group-id]]
    [(rf/subscribe [::all-users tenant-id])
     (rf/subscribe [::section-user-group-ids tenant-id parent-group-id])])
  (fn [[users parent-group-ids] [_ _ _ query]]
    (-> (filter model/service-account? users)
        (section-users parent-group-ids query))))

;; ---------------------------------------------------------------------------
;; Writes
;; ---------------------------------------------------------------------------

(defn scope [tenant-id]
  [:tenant tenant-id])

(defn section-scope
  "The row editors' scope in one group's section: a user is listed in every
  section they have groups in, each with its own draft."
  [tenant-id parent-group-id]
  (conj (scope tenant-id) parent-group-id))

(rf/reg-event-fx ::save-user
  (fn [_ [_ tenant-id parent-group-id user-id {:keys [add remove first-name last-name]}]]
    {:comm/command {:command          :admin/tenant-edit-user
                    :data             (p/assoc-if {:domain-group-id tenant-id
                                                   :user-id         user-id
                                                   :add             (vec add)
                                                   :remove          (vec remove)}
                                                  :first-name first-name
                                                  :last-name last-name)
                    :notify           {:success :changes-saved}
                    :on-success       [::state/discard-draft (section-scope tenant-id parent-group-id) user-id]
                    :finally-dispatch [::fetch-users tenant-id]}}))

(rf/reg-event-fx ::remove-user
  ;; With a `parent-group-id` the user only loses that group's user-groups, which
  ;; is what removing from a section means; without one they leave the tenant.
  (fn [_ [_ tenant-id user-id parent-group-id]]
    {:comm/command {:command          :admin/tenant-remove-user
                    :data             (p/assoc-if {:domain-group-id tenant-id
                                                   :user-id         user-id}
                                                  :parent-group-id parent-group-id)
                    :notify           {:success :changes-saved}
                    :on-success       [::state/discard-draft (section-scope tenant-id parent-group-id) user-id]
                    :finally-dispatch [::fetch-users tenant-id]}}))

(rf/reg-event-fx ::add-user
  (fn [_ [_ tenant-id {:keys [email first-name last-name group-ids]}]]
    {:comm/command {:command          :admin/tenant-add-user
                    :data             (p/assoc-if {:domain-group-id tenant-id
                                                   :user/email      email
                                                   :user/groups     (vec group-ids)}
                                                  :user/first-name (not-empty first-name)
                                                  :user/last-name (not-empty last-name))
                    :notify           {:success :changes-saved}
                    :finally-dispatch [::fetch-users tenant-id]}}))

(rf/reg-event-fx ::add-service-account
  (fn [_ [_ tenant-id {:keys [username group-ids]}]]
    {:comm/command {:command          :admin/tenant-add-service-account
                    :data             {:domain-group-id tenant-id
                                       :username        username
                                       :user-groups     (vec group-ids)}
                    :notify           {:success :changes-saved}
                    :finally-dispatch [::fetch-users tenant-id]}}))

(rf/reg-event-fx ::create-api-key
  (fn [_ [_ tenant-id user-id]]
    {:comm/command {:command    :admin/tenant-create-service-account-api-key
                    :data       {:domain-group-id tenant-id
                                 :user-id         user-id}
                    :on-success [::create-api-key.success tenant-id]
                    :notify     {:success :changes-saved}}}))

(rf/reg-event-fx ::create-api-key.success
  (fn [_ [_ tenant-id _status api-key]]
    {:fx [[:dispatch [::state/show-new-api-key (scope tenant-id) api-key]]
          [:dispatch [::fetch-users tenant-id]]]}))
